import { useClientLogger } from '@landsbankinn/client-logger';
import { useNavigate } from 'react-router';
import {
  ElectronicIdServiceToken,
  type IElectronicIdService,
} from 'src/services/ElectronicIdService';
import { ServicesProvider } from 'src/services/ServicesProvider';
import {
  WindowLocationServiceToken,
  type IWindowLocationService,
} from 'src/services/WindowLocationService';
import {
  AllowedLoginType,
  useApplicationServerContext,
  type LoginServerState,
} from '../../../../state';
import { setPreviousLoginType, submit as submitForm } from '../../utils';
import { type NextStepResponseType, type StartResponseType } from '../types';

const LOGIN_AUDKENNI_APP_ROUTE = '/login/audkenniapp';
type AudkenniAppFormSubmitProps = {
  onCode: (code: string) => void;
  onSubmitting: (isSubmitting: boolean) => void;
  onError: (error: string) => void;
  onXsrf: (xsrfToken: string) => void;
};
export function useAudkenniAppSubmit({
  onCode,
  onSubmitting,
  onError,
  onXsrf,
}: AudkenniAppFormSubmitProps) {
  const [serverState] = useApplicationServerContext() as [LoginServerState];

  const navigate = useNavigate();
  const { logger } = useClientLogger();

  /**
   * Intermediate function to shim Android URL_SCHEME error after refactoring audkenni app flow.
   * Should be removed when https://github.com/landsbankinn/mobilebank-personal/pull/4060 has
   * rolled out to all users and older versions are not supported.
   */
  async function submitUsingFormPost(
    kennitala: string,
    xsrf: string,
    traceparent: string,
  ) {
    onSubmitting(true);
    const electronicIdService =
      ServicesProvider.getSingleton<IElectronicIdService>(
        ElectronicIdServiceToken,
      );

    const { response, data, error } =
      await electronicIdService.fetchAudkenniAppResponse<StartResponseType>({
        kennitala,
        url: '/ui/login/audkenniapp/start',
        xsrf,
        traceparent,
      });
    setPreviousLoginType(AllowedLoginType.AudkenniApp);
    if (response.status === 400 && error && error.error_message) {
      onError(error.error_message[0]);
      onSubmitting(false);
    } else if (data) {
      onCode(data.code);
      onXsrf(data.xsrfToken);
    }
    const form = document.querySelector('form');
    if (form !== null) {
      setPreviousLoginType(AllowedLoginType.AudkenniApp);
      submitForm(form);
    }
  }

  async function submitUsingFetch(
    kennitala: string,
    xsrf: string,
    traceparent: string,
  ) {
    // Indicate two step login is started
    onSubmitting(true);
    const windowLocationService =
      ServicesProvider.getSingleton<IWindowLocationService>(
        WindowLocationServiceToken,
        navigate,
      );
    const electronicIdService =
      ServicesProvider.getSingleton<IElectronicIdService>(
        ElectronicIdServiceToken,
      );

    // Get response as json
    const { response, data, error } =
      await electronicIdService.fetchAudkenniAppResponse<NextStepResponseType>({
        kennitala,
        url: '/ui/login/audkenniApp',
        xsrf,
        traceparent,
      });

    setPreviousLoginType(AllowedLoginType.AudkenniApp);
    if (response.status === 400 && error) {
      logger.warn('Bad request during Audkenni App', { ...error });
      let msg = '';
      if (error.kennitala) msg = error.kennitala;
      if (error.error_message) msg = error.error_message[0];
      onError(msg);
      onSubmitting(false);
    } else if (data) {
      logger.trace('Response from Electronic Id Service', { data });
      // Handle only known LOGIN_AUDKENNI_APP nextstep url
      if (data.nextStep.includes(LOGIN_AUDKENNI_APP_ROUTE)) {
        // Store xsrf & contextId in state
        onXsrf(data.xsrfToken);
        logger.message('Audkenni App authentication started', { ...data });
        navigate(LOGIN_AUDKENNI_APP_ROUTE);
      } else {
        logger.warn('Unsupported nextStep in Audkenni App response', {
          ...data,
        });
        windowLocationService.goto(data.nextStep, navigate);
      }
    }
  }

  async function submit(
    kennitala: string,
    method: 'POST' | 'XMLHttpRequest',
    xsrf?: string,
  ) {
    const xsrfToken: string =
      xsrf || serverState.forms.audkenniApp.fields.xsrf.value || '';
    const traceparentValue: string = serverState.traceparent;
    if (method === 'POST') {
      return submitUsingFormPost(kennitala, xsrfToken, traceparentValue);
    } else {
      return submitUsingFetch(kennitala, xsrfToken, traceparentValue);
    }
  }
  return submit;
}
